/* Smart Terminals — Product catalog (shared across home section and detail pages) */

// ST flagships (own products with dedicated detail pages)
export const PRODUCTS = ["smarterp", "finance-st"];

// Additional catalog powered by our partner FocusR — shown on /products only.
export const PARTNER_PRODUCTS = ["integrahub", "invoicez", "delzee", "spot", "workflow", "dealerportal"];

const productsContent = {
  en: {
    sectionKicker: "// Our Products",
    sectionTitle1: "Software built for enterprise,",
    sectionTitle2: "delivered locally.",
    sectionBody: "Two flagship platforms from Smart Terminals plus a curated catalog powered by our strategic partner FocusR — engineered for scale, security, and modern operations.",
    sectionCta: "Browse full catalog",
    partnerBadge: "By FocusR · Partner",
    ownBadge: "By Smart Terminals",
    common: {
      visitProduct: "Open product",
      learnMore: "Learn more",
      tryDemo: "Try live demo",
      backToProducts: "← All products",
      keyModules: "Key modules",
      keyFeatures: "Key features",
      whoFor: "Who it's for",
      why: "Why teams choose it",
      cta: "Ready to evaluate?",
      ctaBody: "Open the live product or talk to our team for a private walkthrough tailored to your stack.",
      ctaContact: "Talk to sales",
    },
    catalog: {
      "smarterp": {
        slug: "smarterp", name: "SmartERP", version: "v1.0", by: "st",
        tagline: "Enterprise precision. At scale.",
        short: "Cloud-native, multi-tenant ERP — Finance, HR and Supply Chain unified under one secure platform with AI assistance and MFA by default.",
        url: "https://smarterp-st.com", accent: "red",
        badges: ["Cloud-Native", "Multi-Tenant", "AI-Powered", "MFA Secure"],
        modules: [
          { code: "01", title: "Finance",       desc: "GL · AR · AP — full general ledger, receivables and payables with audit-ready trails." },
          { code: "02", title: "Human Resources", desc: "Payroll, leaves, attendance and employee lifecycle — tuned for Omanization rules." },
          { code: "03", title: "Supply Chain", desc: "Inventory, purchase orders and sales orders across warehouses and entities." },
          { code: "04", title: "HSE & EAM",    desc: "Health, safety, environment and enterprise asset management for operations-heavy industries." },
        ],
        features: [
          "Multi-tenant cloud architecture — onboard new entities in minutes.",
          "AI assistance for posting, reconciliation and anomaly detection.",
          "TOTP + Email OTP multi-factor authentication by default.",
          "Audit-ready logs and role-based access controls.",
          "Open APIs for integration with banks, POS and government portals.",
        ],
        whoFor: ["Mid-market to enterprise CFOs and COOs", "Government entities digitizing back-office", "Multi-entity groups in the GCC"],
        bonus: {
          title: "Free tool · No signup",
          body: "Cash Flow Health Check — get an AI-powered CFO assessment in 30 seconds.",
          url: "https://smarterp-st.com/cash-health-check",
          label: "Try the Health Check →",
        },
      },
      "finance-st": {
        slug: "finance-st", name: "Finance-ST", version: "v1.0", by: "st",
        tagline: "AI-driven CFO intelligence.",
        short: "Enterprise FP&A and treasury — forecasting, cash-flow planning and analytics built for finance leaders who need answers in seconds.",
        url: "https://finance-st.com", accent: "teal",
        badges: ["AI-Powered", "Treasury", "FP&A", "Multi-Entity"],
        modules: [
          { code: "01", title: "AI Forecasting",  desc: "Roll your historicals into 13-week, 12-month and multi-year forecasts with confidence bands." },
          { code: "02", title: "Cash-Flow Planning", desc: "Scenario modelling for receivables, payables and treasury movements across accounts." },
          { code: "03", title: "Treasury Dashboards", desc: "Real-time KPIs on liquidity, runway, ageing and concentration risk." },
          { code: "04", title: "Consolidation",  desc: "Multi-entity, multi-currency rollups with audit-ready reports for board and regulators." },
        ],
        features: [
          "Connect your books and bank feeds; insights in minutes, not weeks.",
          "AI-driven anomaly detection on expenses and revenue trends.",
          "Board-ready dashboards exportable to PDF and presentations.",
          "Scenario planner — model 'what if oil drops 15%' in one click.",
          "Bank-grade security with role-based access.",
        ],
        whoFor: ["CFOs and finance controllers", "Treasury and FP&A teams", "Family offices and holding groups"],
        bonus: null,
      },
      // FocusR partner products — external links, no dedicated detail page
      "integrahub": {
        slug: "integrahub", name: "Integrahub", by: "focusr",
        tagline: "One integration layer to link your systems, data, and teams — intuitively.",
        short: "Low-code connectors and enterprise-grade API mesh to unify apps and data behind a high-performance integration layer.",
        url: "https://focusrtech.com/products/integrahub", accent: "black",
        badges: ["Low-Code", "API Mesh", "Enterprise"],
      },
      "invoicez": {
        slug: "invoicez", name: "InvoicEZ", by: "focusr",
        tagline: "Turn every gate entry into an instant, accurate GR.",
        short: "API-powered AI automation for the full gate-entry to Goods Receipt cycle — eliminate manual data entry and reconciliation errors.",
        url: "https://focusrtech.com/products/invoicez", accent: "black",
        badges: ["AI Automation", "GR Automation", "OCR"],
      },
      "delzee": {
        slug: "delzee", name: "Delzee", by: "focusr",
        tagline: "Plan, allocate, track, and deliver — dispatch made simple.",
        short: "Dispatch planner that enables transporter bidding, vehicle allocation, real-time tracking, and on-time delivery visibility.",
        url: "https://focusrtech.com/products/delzee", accent: "black",
        badges: ["Logistics", "Real-Time", "Bidding"],
      },
      "spot": {
        slug: "spot", name: "Spot", by: "focusr",
        tagline: "Corporate loan management without spreadsheet risk.",
        short: "IND AS-compliant corporate loan platform — initial recognition, EMI schedules, approvals, and ERP-ready journal entries.",
        url: "https://focusrtech.com/products/spot", accent: "black",
        badges: ["IND AS", "Loans", "Finance"],
      },
      "workflow": {
        slug: "workflow", name: "Workflow", by: "focusr",
        tagline: "Sequential file-note e-approvals for governance teams.",
        short: "SLA-routed approvals with magic-link decisions, finance controllership gates and verified PDF certificates.",
        url: "https://focusrtech.com/products/workflow", accent: "black",
        badges: ["Governance", "e-Approvals", "SLA"],
      },
      "dealerportal": {
        slug: "dealerportal", name: "Dealer Portal", by: "focusr",
        tagline: "A secure hub for dealers & customers — orders, payments, tracking.",
        short: "One place for your channel network to place orders, view payments, and track shipments in real time.",
        url: "https://focusrtech.com/products/dealer-portal", accent: "black",
        badges: ["Channel", "Payments", "Tracking"],
      },
    },
  },
  ar: {
    sectionKicker: "// منتجاتنا",
    sectionTitle1: "برمجيات للمؤسسات،",
    sectionTitle2: "بتنفيذ محلي.",
    sectionBody: "منتجان رئيسيان من سمارت تيرمنلز إلى جانب مجموعة منتجات مختارة مدعومة من شريكنا الاستراتيجي FocusR.",
    sectionCta: "تصفّح كامل الكتالوج",
    partnerBadge: "من FocusR · شريك",
    ownBadge: "من سمارت تيرمنلز",
    common: {
      visitProduct: "افتح المنتج",
      learnMore: "اعرف المزيد",
      tryDemo: "جرّب النسخة الحية",
      backToProducts: "→ كل المنتجات",
      keyModules: "الوحدات الرئيسية",
      keyFeatures: "أبرز المزايا",
      whoFor: "لمن هذا المنتج",
      why: "لماذا يختاره الفرق",
      cta: "جاهز للتقييم؟",
      ctaBody: "افتح النسخة الحية أو تواصل مع فريقنا لجولة خاصة.",
      ctaContact: "تحدث مع المبيعات",
    },
    catalog: {
      "smarterp": {
        slug: "smarterp", name: "SmartERP", version: "v1.0", by: "st",
        tagline: "دقة المؤسسات. على نطاق واسع.",
        short: "نظام ERP سحابي متعدد المستأجرين — المالية والموارد البشرية وسلسلة الإمداد في منصة واحدة آمنة.",
        url: "https://smarterp-st.com", accent: "red",
        badges: ["سحابي", "متعدد المستأجرين", "ذكاء اصطناعي", "مصادقة آمنة"],
        modules: [
          { code: "٠١", title: "المالية",        desc: "دفتر أستاذ · ذمم مدينة ودائنة مع سجلات تدقيق." },
          { code: "٠٢", title: "الموارد البشرية", desc: "الرواتب والإجازات ودورة حياة الموظف — مهيأ للتعمين." },
          { code: "٠٣", title: "سلسلة الإمداد",  desc: "المخزون وطلبات الشراء والمبيعات." },
          { code: "٠٤", title: "السلامة والأصول", desc: "إدارة الصحة والسلامة والأصول." },
        ],
        features: ["بنية سحابية متعددة المستأجرين.", "مساعد ذكاء اصطناعي.", "مصادقة متعددة العوامل افتراضياً.", "سجلات تدقيق وضوابط وصول.", "واجهات برمجية مفتوحة."],
        whoFor: ["المدراء الماليون للمؤسسات", "الجهات الحكومية", "المجموعات متعددة الكيانات"],
        bonus: { title: "أداة مجانية", body: "فحص صحة التدفق النقدي.", url: "https://smarterp-st.com/cash-health-check", label: "جرّب الآن ←" },
      },
      "finance-st": {
        slug: "finance-st", name: "Finance-ST", version: "v1.0", by: "st",
        tagline: "ذكاء مدير مالي مدعوم بالذكاء الاصطناعي.",
        short: "تخطيط مالي وإدارة خزينة على مستوى المؤسسات.",
        url: "https://finance-st.com", accent: "teal",
        badges: ["ذكاء اصطناعي", "خزينة", "تخطيط مالي", "متعدد الكيانات"],
        modules: [
          { code: "٠١", title: "توقعات ذكية", desc: "توقعات مالية بنطاقات ثقة." },
          { code: "٠٢", title: "تخطيط التدفق النقدي", desc: "نمذجة سيناريوهات مالية." },
          { code: "٠٣", title: "لوحات الخزينة", desc: "مؤشرات لحظية." },
          { code: "٠٤", title: "التجميع", desc: "متعدد الكيانات والعملات." },
        ],
        features: ["اربط البنوك واحصل على رؤى فورية.", "كشف الشذوذ بالذكاء الاصطناعي.", "لوحات جاهزة للمجلس.", "مخطط سيناريوهات.", "أمان بمستوى البنوك."],
        whoFor: ["المدراء الماليون", "فرق الخزينة", "المكاتب العائلية"],
        bonus: null,
      },
      "integrahub": { slug: "integrahub", name: "Integrahub", by: "focusr", tagline: "طبقة تكامل موحّدة تربط أنظمتك وبياناتك وفرقك.", short: "موصلات منخفضة الكود وشبكة APIs لتوحيد التطبيقات والبيانات.", url: "https://focusrtech.com/products/integrahub", accent: "black", badges: ["Low-Code", "API Mesh", "Enterprise"] },
      "invoicez":   { slug: "invoicez",   name: "InvoicEZ",   by: "focusr", tagline: "من دخول البوابة إلى إيصال البضائع فوراً وبدقة.", short: "أتمتة مدعومة بالذكاء الاصطناعي لدورة GR كاملة.", url: "https://focusrtech.com/products/invoicez", accent: "black", badges: ["أتمتة AI", "GR", "OCR"] },
      "delzee":     { slug: "delzee",     name: "Delzee",     by: "focusr", tagline: "خطّط ووزّع وتتبّع وسلّم — الشحن ببساطة.", short: "مخطط شحن مع مزاد ناقلين وتخصيص مركبات وتتبع لحظي.", url: "https://focusrtech.com/products/delzee", accent: "black", badges: ["لوجستيات", "لحظي", "مزاد"] },
      "spot":       { slug: "spot",       name: "Spot",       by: "focusr", tagline: "إدارة القروض المؤسسية بلا مخاطر جداول البيانات.", short: "منصة قروض متوافقة مع IND AS مع جداول أقساط وقيود جاهزة.", url: "https://focusrtech.com/products/spot", accent: "black", badges: ["IND AS", "قروض", "مالية"] },
      "workflow":   { slug: "workflow",   name: "Workflow",   by: "focusr", tagline: "اعتمادات ملاحظات ملف متسلسلة لفرق الحوكمة.", short: "اعتمادات SLA مع روابط سحرية وشهادات PDF موثّقة.", url: "https://focusrtech.com/products/workflow", accent: "black", badges: ["حوكمة", "اعتمادات", "SLA"] },
      "dealerportal": { slug: "dealerportal", name: "Dealer Portal", by: "focusr", tagline: "بوابة آمنة للوكلاء والعملاء — طلبات ومدفوعات وتتبع.", short: "مكان واحد لشبكة وكلائك لإدارة الطلبات والمدفوعات والشحنات.", url: "https://focusrtech.com/products/dealer-portal", accent: "black", badges: ["قنوات", "مدفوعات", "تتبع"] },
    },
  },
};

export default productsContent;
